import { UserIcon } from "@heroicons/react/24/outline"
import type { AgendaSlotDto } from "~/ui-rtk/api/agendaApi"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import type { AssignableUser } from "../hooks/useAssignableUsers"
import { initials } from "../utils"

export const UNASSIGNED_FILTER = "__unassigned__"

/**
 * Linear-style assignee filter: a row of avatar chips (initials). Clicking
 * toggles that person in/out of the filter (multi-select); unselected avatars
 * dim while a filter is active. Only shows people who actually have slots in
 * the loaded window (+ an "Unassigned" chip when relevant) so the row stays
 * short.
 */
export default function AssigneeFilter({
  slots,
  users,
  selected,
  onToggle,
  onClear,
}: {
  slots: AgendaSlotDto[]
  users: AssignableUser[]
  selected: Set<string>
  onToggle: (id: string) => void
  onClear: () => void
}) {
  const assigneeIds = new Set(
    slots.map(slot => slot.assigneeId).filter(Boolean),
  )
  const visibleUsers = users.filter(user => assigneeIds.has(user.id))
  const hasUnassigned = slots.some(slot => !slot.assigneeId)
  const isFiltering = selected.size > 0

  if (visibleUsers.length === 0 && !hasUnassigned) return null

  return (
    <div className="flex items-center gap-1.5">
      {visibleUsers.map(user => {
        const isActive = selected.has(user.id)
        const count = slots.filter(slot => slot.assigneeId === user.id).length
        return (
          <button
            key={user.id}
            type="button"
            title={`${user.name} · ${count} slot${count === 1 ? "" : "s"}`}
            onClick={() => onToggle(user.id)}
            className={cn(
              "flex h-8 w-8 items-center justify-center rounded-full border text-xs font-semibold transition-all duration-150",
              "border-system-blue-600/60 bg-system-blue-800 text-system-gray-200 hover:border-system-brand-pink-500/60",
              isActive &&
                "border-system-brand-pink-500 bg-system-brand-pink-600/20 text-white ring-2 ring-system-brand-pink-500/40",
              isFiltering && !isActive && "opacity-40 hover:opacity-80",
            )}
          >
            {initials(user.name)}
          </button>
        )
      })}
      {hasUnassigned && (
        <button
          type="button"
          title="Unassigned"
          onClick={() => onToggle(UNASSIGNED_FILTER)}
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-full border border-dashed transition-all duration-150",
            "border-system-gray-500/60 text-system-gray-400 hover:border-system-brand-pink-500/60",
            selected.has(UNASSIGNED_FILTER) &&
              "border-system-brand-pink-500 bg-system-brand-pink-600/20 text-white ring-2 ring-system-brand-pink-500/40",
            isFiltering &&
              !selected.has(UNASSIGNED_FILTER) &&
              "opacity-40 hover:opacity-80",
          )}
        >
          <UserIcon className="h-4 w-4" />
        </button>
      )}
      {isFiltering && (
        <button
          type="button"
          className="ml-1 text-xs text-system-gray-400 hover:text-white"
          onClick={onClear}
        >
          Clear
        </button>
      )}
    </div>
  )
}
